import { Link } from "@tanstack/react-router";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { JsonLd } from "@/components/JsonLd";
import { breadcrumbSchema, faqPageSchema, serviceSchema } from "@/lib/schemas";

export interface PlatformData {
  name: string;
  tagline: string;
  intro: string;
  sections: { heading: string; paragraphs: string[] }[];
  features: string[];
  faq: { q: string; a: string }[];
}

export function PlatformPage({
  data,
  path,
  breadcrumbCategory = "Platformy",
}: {
  data: PlatformData;
  path: string;
  breadcrumbCategory?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  const breadcrumbs = breadcrumbSchema([
    { name: "Strona główna", path: "/" },
    { name: breadcrumbCategory, path: path.split("/").slice(0, -1).join("/") || "/" },
    { name: data.name, path },
  ]);

  const service = serviceSchema({
    name: `${data.name} — wdrożenia i integracje`,
    description: data.intro,
    path,
  });

  const faq = faqPageSchema(data.faq);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <JsonLd data={breadcrumbs} />
      <JsonLd data={service} />
      <JsonLd data={faq} />
      <Header />

      <article>
        <section ref={ref} className="relative overflow-hidden pt-36 pb-20">
          <motion.div
            style={{ y: bgY }}
            className="bg-mesh absolute inset-0 -z-10 opacity-60"
          />
          <div className="absolute left-1/2 top-32 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />

          <div className="mx-auto max-w-4xl px-6">
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground">Strona główna</Link>
              <span>/</span>
              <span>{breadcrumbCategory}</span>
              <span>/</span>
              <span className="text-foreground">{data.name}</span>
            </nav>
            <p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
              Wdrożenia & integracje
            </p>
            <h1 className="font-display text-4xl font-semibold leading-tight md:text-6xl">
              {data.name} —{" "}
              <span className="text-gradient">{data.tagline}</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">{data.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="shadow-glow">
                <Link to="/" hash="kontakt">
                  Umów konsultację <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/">Wróć na stronę główną</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-3xl px-6">
            <div className="grid gap-3 sm:grid-cols-2">
              {data.features.map((f) => (
                <div
                  key={f}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card p-4"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" />
                  <span className="text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="mx-auto max-w-3xl space-y-14 px-6">
            {data.sections.map((s) => (
              <div key={s.heading}>
                <h2 className="font-display text-2xl font-semibold md:text-3xl">{s.heading}</h2>
                <div className="mt-5 space-y-5 leading-relaxed text-muted-foreground">
                  {s.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="font-display text-2xl font-semibold md:text-3xl">
              Najczęściej zadawane pytania
            </h2>
            <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
              {data.faq.map((f) => (
                <details key={f.q} className="group p-5">
                  <summary className="flex cursor-pointer items-center justify-between font-medium">
                    {f.q}
                    <span className="ml-4 text-primary transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden py-24">
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-background to-primary-glow/15" />
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Gotowy na wdrożenie {data.name}?
            </h2>
            <p className="mt-4 text-muted-foreground">
              Umów bezpłatną konsultację — przeanalizuję Twój projekt i zaproponuję plan działania.
            </p>
            <Button asChild size="lg" className="mt-8 shadow-glow">
              <Link to="/" hash="kontakt">
                Umów konsultację <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </article>

      <Footer />
      <Toaster richColors position="top-right" />
    </div>
  );
}
